import { useCallback, useRef, useEffect } from 'react';
import { AnalyticsService, AnalyticsTrackingEvent } from '../../analytics';
import {
  ConversationAccessPointType,
  ConversationAccessPointProperties,
  PageContextProperties,
  getPageNameFromPath,
  getPageTypeFromPath,
  truncateQuery,
} from '../tracking';
import { PageContext } from '../types/widget';

interface UseConciergeTrackingProps {
  conversationId: string | null;
  pageContext?: PageContext;
  appId?: string | null;
}

/**
 * Hook for tracking concierge conversation analytics events.
 *
 * Tracks two main events:
 * 1. CONVERSATION_ACCESS_POINT - Where the user started/continued the conversation
 * 2. PAGE_CONTEXT - The page context where the conversation is happening
 *
 * Usage:
 * const { trackConversationStart } = useConciergeTracking({ conversationId, pageContext });
 * trackConversationStart('search_bar', 'my query', isNewConversation);
 */
export const useConciergeTracking = ({
  conversationId,
  pageContext,
  appId,
}: UseConciergeTrackingProps) => {
  // Track the last page context we tracked to avoid duplicate events
  const lastTrackedPageRef = useRef<string | null>(null);
  // Track if we've already tracked an access point for this conversation
  const lastTrackedConversationRef = useRef<string | null>(null);

  /**
   * Track a conversation access point event.
   * Fire this when a user initiates or continues a conversation.
   *
   * @param accessPoint - Where the conversation was initiated from
   * @param query - The query/message being sent (optional, truncated to 200 chars)
   * @param isNewConversation - Whether this is a new conversation (no prior messages)
   */
  const trackConversationAccessPoint = useCallback(
    (
      accessPoint: ConversationAccessPointType,
      query?: string,
      isNewConversation?: boolean
    ) => {
      // Don't track if we don't have a conversation ID yet
      // The tracking will be called again once we have one
      if (!conversationId) {
        return;
      }

      // Only fire the access point event once per conversation
      if (lastTrackedConversationRef.current === conversationId) {
        return;
      }
      lastTrackedConversationRef.current = conversationId;

      const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

      const properties: ConversationAccessPointProperties = {
        accessPoint,
        conversationId,
        pageUrl,
        appVersion: 'v2',
        ...(query && { query: truncateQuery(query) }),
        ...(isNewConversation !== undefined && { isNewConversation }),
        ...(pageContext?.stacklineSku && { stacklineSku: pageContext.stacklineSku }),
        ...(appId && { appId }),
      };

      // Fire the event asynchronously (non-blocking)
      AnalyticsService.track(
        AnalyticsTrackingEvent.CONCIERGE_CONVERSATION_ACCESS_POINT,
        properties
      ).catch(() => {
        // Silently ignore tracking errors to not affect user experience
      });
    },
    [conversationId, pageContext?.stacklineSku, appId]
  );

  /**
   * Track a page context event.
   * Fire this when a conversation starts or when the page changes during an active conversation.
   */
  const trackPageContext = useCallback(() => {
    if (!conversationId) {
      return;
    }

    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

    // Don't track the same page twice for the same conversation
    const pageKey = `${conversationId}:${pageUrl}`;
    if (lastTrackedPageRef.current === pageKey) {
      return;
    }
    lastTrackedPageRef.current = pageKey;

    const pageName = getPageNameFromPath(pathname);
    const pageType = getPageTypeFromPath(pathname);

    const properties: PageContextProperties = {
      conversationId,
      pageUrl,
      pageName,
      pageType,
      appVersion: 'v2',
      ...(pageContext?.stacklineSku && { stacklineSku: pageContext.stacklineSku }),
      ...(appId && { appId }),
    };

    // Fire the event asynchronously (non-blocking)
    AnalyticsService.track(
      AnalyticsTrackingEvent.CONCIERGE_PAGE_CONTEXT,
      properties
    ).catch(() => {
      // Silently ignore tracking errors to not affect user experience
    });
  }, [conversationId, pageContext?.stacklineSku, appId]);

  /**
   * Combined function to track both access point and page context.
   * Call this when a conversation message is sent.
   *
   * @param accessPoint - Where the conversation was initiated from
   * @param query - The query/message being sent
   * @param isNewConversation - Whether this is a new conversation
   */
  const trackConversationStart = useCallback(
    (
      accessPoint: ConversationAccessPointType,
      query?: string,
      isNewConversation?: boolean
    ) => {
      // Track access point
      trackConversationAccessPoint(accessPoint, query, isNewConversation);

      // Track page context when starting a conversation
      trackPageContext();
    },
    [trackConversationAccessPoint, trackPageContext]
  );

  // Track page context changes during an active conversation
  useEffect(() => {
    if (!conversationId) {
      // Reset tracking state when conversation changes
      lastTrackedPageRef.current = null;
      lastTrackedConversationRef.current = null;
      return;
    }

    // Set up listener for route changes (for SPAs)
    const handleRouteChange = () => {
      trackPageContext();
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [conversationId, trackPageContext]);

  return {
    trackConversationStart,
    trackConversationAccessPoint,
    trackPageContext,
  };
};
